import { useCallback } from "react";
import { Check, ChevronDown } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { muteVideoRef } from "@/lib/muteVideoRef";
import { cn } from "@/lib/utils";
import { Button } from "@/components/flowly/infra/Button/Button";
import { TRUST_BADGES } from "./trustBadges";
import { FLOWS_ANCHOR_ID } from "@/components/flowly/Landing/LandingFlowsShowcase/constants";
import { HERO_VIDEO_CROSSFADE_DURATION_MS } from "./constants";
import { useHeroCrossfade } from "./useHeroCrossfade";

export function LandingHero() {
  const { t, isRTL } = useLanguage();
  const { videoRef, fading, initialSrc } = useHeroCrossfade();

  // Stable ref callback (runs once on mount, not per render) — see
  // TalkingAvatarHeroShowcase for why an inline ref would re-mute on re-render.
  const attachVideo = useCallback(
    (el: HTMLVideoElement | null) => {
      videoRef.current = el;
      muteVideoRef(el);
    },
    [videoRef],
  );

  const handleScrollToFlows = () => {
    requestAnimationFrame(() => {
      document
        .getElementById(FLOWS_ANCHOR_ID)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  return (
    <section className="relative h-screen flex flex-col items-center justify-center overflow-hidden">
      {/* Video background — single element, source-swapped for iOS compat */}
      <video
        ref={attachVideo}
        className={cn(
          "absolute inset-0 w-full h-full object-cover z-0",
          fading ? "opacity-0" : "opacity-100",
        )}
        style={{
          transitionProperty: "opacity",
          transitionDuration: `${HERO_VIDEO_CROSSFADE_DURATION_MS / 2}ms`,
          transitionTimingFunction: "ease-in-out",
        }}
        src={initialSrc}
        autoPlay
        loop
        muted
        playsInline
      />

      {/* Gradient overlay — darker at bottom for text legibility */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-black/30 z-[1]" />

      {/* Subtle brand-tinted radial glow */}
      <div
        className="absolute inset-0 z-[1]"
        style={{
          background:
            "radial-gradient(ellipse at center, hsl(15 90% 58% / 0.12), transparent 70%)",
        }}
      />

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center text-center px-4 pt-20 pb-8 md:pt-24 md:pb-10">
        {/* Title — middle word rendered in brand gradient for emphasis. */}
        <h1
          className="opacity-0 animate-fade-in text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold leading-[1.05] tracking-tight text-white"
          style={{ animationDelay: "200ms" }}
        >
          {t.landing.heroTitlePrefix}
          <span className="bg-gradient-to-r from-brand to-brand-secondary bg-clip-text text-transparent">
            {t.landing.heroTitleAccent}
          </span>
          {t.landing.heroTitleSuffix}
        </h1>

        {/* Subtitle — higher contrast (white/85) and larger so it actually
            reads on the video background. */}
        <p
          className="opacity-0 animate-fade-in mt-6 md:mt-8 text-lg md:text-xl lg:text-2xl text-white/85 max-w-2xl leading-relaxed"
          style={{ animationDelay: "350ms" }}
        >
          {t.landing.heroSubtitle}
        </p>

        {/* CTAs */}
        <div
          className={cn(
            "opacity-0 animate-fade-in mt-10 md:mt-12 flex flex-col sm:flex-row items-center gap-4",
            isRTL && "sm:flex-row-reverse",
          )}
          style={{ animationDelay: "500ms" }}
        >
          <Button
            variant="accent"
            size="lg"
            onClick={handleScrollToFlows}
            className="w-full sm:w-auto animate-glow-pulse"
          >
            {t.landing.startFree}
          </Button>
        </div>

        {/* Microcopy */}
        <div
          className={cn(
            "opacity-0 animate-fade-in mt-5 flex items-center gap-3 text-sm",
            isRTL && "flex-row-reverse",
          )}
          style={{ animationDelay: "600ms" }}
        >
          <span className="flex items-center gap-1.5">
            <Check className="h-4 w-4 text-emerald-400" />
            <span className="text-white/80">{t.landing.heroMicrocopyCredits}</span>
          </span>
          <span className="text-white/30">|</span>
          <span className="flex items-center gap-1.5">
            <Check className="h-4 w-4 text-emerald-400" />
            <span className="text-white/80">{t.landing.heroMicrocopyNoCard}</span>
          </span>
        </div>
      </div>

      {/* Trust badges bar — punchier glass pill (bg-white/10, ring + shadow). */}
      <div
        className="opacity-0 animate-fade-in relative z-10 w-full mt-8 md:mt-12 px-4"
        style={{ animationDelay: "700ms" }}
      >
        <div
          className={cn(
            "flex flex-wrap justify-center gap-4 md:gap-0 md:divide-x md:divide-white/15 bg-white/10 backdrop-blur-md border border-white/20 ring-1 ring-white/5 shadow-2xl shadow-black/40 rounded-2xl mx-auto max-w-4xl px-6 md:px-10 py-4",
            isRTL && "flex-row-reverse md:divide-x-reverse",
          )}
        >
          {TRUST_BADGES.map((badge) => (
            <div
              key={badge.labelKey}
              className="flex items-center gap-2 md:px-6"
            >
              <badge.icon className="h-4 w-4 text-brand" />
              <span className="text-sm font-medium text-white/85">
                {t.landing[badge.labelKey as keyof typeof t.landing]}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll cue — fills lower-third dead-space and gives a clear hint.
          Wrapped in a full-width flex centering parent so animate-fade-in's
          terminal `transform: none` can't clobber our centering translate. */}
      <div className="absolute inset-x-0 bottom-6 md:bottom-10 z-10 flex justify-center pointer-events-none">
        <button
          type="button"
          onClick={handleScrollToFlows}
          aria-label={t.landing.heroScrollHint}
          className="opacity-0 animate-fade-in pointer-events-auto flex flex-col items-center gap-1.5 text-white/70 hover:text-white transition-colors cursor-pointer"
          style={{ animationDelay: "900ms" }}
        >
          <span className="text-[10px] md:text-xs uppercase tracking-[0.2em] font-semibold">
            {t.landing.heroScrollHint}
          </span>
          <ChevronDown className="h-5 w-5 animate-bounce" />
        </button>
      </div>
    </section>
  );
}
